/**
 * When the selection spans multiple paragraphs (one or more blank lines), wrap each
 * block separately so bold/italic/strike apply per paragraph (CommonMark does not
 * treat emphasis across blank lines as one span).
 *
 * @param {string} selected
 * @param {string} before e.g. `**`
 * @param {string} after e.g. `**`
 */
export function wrapMarkdownSelection(selected, before, after) {
  const normalized = selected.replace(/\r\n/g, '\n');
  if (!/\n\n/.test(normalized)) {
    return before + selected + after;
  }
  return normalized
    .split(/\n\n+/)
    .map((block) => {
      if (block === '') return '';
      return before + block + after;
    })
    .join('\n\n');
}

/**
 * Cmd/Ctrl+B → **bold**, Cmd/Ctrl+I → *italic*, Cmd/Ctrl+Shift+X → ~~strikethrough~~
 * Wraps the current selection, or inserts empty markers with the caret between them.
 * Multi-paragraph selections are wrapped per paragraph (split on blank lines).
 *
 * @param {React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>} e
 * @param {string} currentValue
 * @param {(next: string) => void} onValueChange
 * @returns {boolean} true if handled
 */
export function handleMarkdownShortcut(e, currentValue, onValueChange) {
  const mod = e.metaKey || e.ctrlKey;
  if (!mod) return false;

  const key = e.key.toLowerCase();
  let before = '';
  let after = '';

  if (key === 'b' && !e.shiftKey && !e.altKey) {
    before = '**';
    after = '**';
  } else if (key === 'i' && !e.shiftKey && !e.altKey) {
    before = '*';
    after = '*';
  } else if (key === 'x' && e.shiftKey && !e.altKey) {
    before = '~~';
    after = '~~';
  } else {
    return false;
  }

  e.preventDefault();

  const ta = e.currentTarget;
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const selected = currentValue.slice(start, end);

  let newValue;
  let selStart;
  let selEnd;

  if (start === end) {
    newValue = currentValue.slice(0, start) + before + after + currentValue.slice(end);
    selStart = selEnd = start + before.length;
  } else {
    const wrapped = wrapMarkdownSelection(selected, before, after);
    newValue = currentValue.slice(0, start) + wrapped + currentValue.slice(end);
    selStart = start;
    selEnd = start + wrapped.length;
  }

  onValueChange(newValue);

  setTimeout(() => {
    if (typeof document !== 'undefined' && document.contains(ta)) {
      ta.focus();
      ta.setSelectionRange(selStart, selEnd);
    }
  }, 0);

  return true;
}

/** @deprecated Use handleMarkdownShortcut */
export const handleBodyMarkdownShortcut = handleMarkdownShortcut;
